import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/images/fgfgv-v.png';
import styles from './Navbar.module.css';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const logoBarRef = useRef(null);
  const topBarRef = useRef(null);
  const navbarRef = useRef(null);
  const scrolledRef = useRef(false);

  // Measure top bar + logo bar height and set CSS variable for the slide-up offset
  // When scrolled, we slide the entire navbar up so only the nav menu remains visible
  const updateLogoBarHeight = useCallback(() => {
    if (logoBarRef.current && topBarRef.current && navbarRef.current) {
      const topBarHeight = topBarRef.current.offsetHeight;
      const logoBarHeight = logoBarRef.current.offsetHeight;
      const totalOffset = topBarHeight + logoBarHeight;
      navbarRef.current.style.setProperty('--logo-bar-height', `${totalOffset}px`);
    }
  }, []);

  useEffect(() => {
    updateLogoBarHeight();
    window.addEventListener('resize', updateLogoBarHeight);
    // Re-measure after fonts load
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(updateLogoBarHeight);
    }
    return () => window.removeEventListener('resize', updateLogoBarHeight);
  }, [updateLogoBarHeight]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const wasScrolled = scrolledRef.current;

          // Hysteresis: different thresholds for activating vs deactivating
          // This prevents toggling when scrollY is near the threshold
          let newScrolled = wasScrolled;
          if (wasScrolled && currentScrollY < 10) {
            newScrolled = false;
          } else if (!wasScrolled && currentScrollY > 50) {
            newScrolled = true;
          }

          if (newScrolled !== wasScrolled) {
            scrolledRef.current = newScrolled;
            setScrolled(newScrolled);
          }

          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Convert Western numerals to Arabic/Eastern numerals
  const toArabicNumerals = (text) => {
    if (i18n.language !== 'ar') return text;
    
    const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return text.replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.body.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const isActive = (path) => {
    return location.pathname === path ? styles.navLinkActive : '';
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <nav ref={navbarRef} className={`${styles.navbar} ${scrolled ? styles.navbarScrolled : ''}`}>
      {/* Top Bar with Contact Info */}
      <div ref={topBarRef} className={styles.navbarTop}>
        <div className={styles.navbarContainer}>
          <div className={styles.contactInfo}>
            <a href={`tel:${t('contact.phone1')}`} className={styles.contactItem}>
              <svg className={styles.icon} fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className={styles.contactText}>{toArabicNumerals(t('contact.phone1'))}</span>
            </a>
            <a href={`tel:${t('contact.phone2')}`} className={styles.contactItem}>
              <svg className={styles.icon} fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <span className={styles.contactText}>{toArabicNumerals(t('contact.phone2'))}</span>
            </a>
            <a href={`mailto:${t('contact.email')}`} className={styles.contactItem}>
              <svg className={styles.icon} fill="currentColor" viewBox="0 0 20 20">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <span className={styles.contactText}>{t('contact.email')}</span>
            </a>
          </div>
          <div className={styles.topBarRight}>
            <button className={styles.languageToggleTop} onClick={toggleLanguage}>
              <span>{i18n.language === 'en' ? 'العربية' : 'English'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Logo Bar */}
      <div ref={logoBarRef} className={styles.logoBar}>
        <div className={styles.navbarContainer}>
          <Link to="/" className={styles.logoLink}>
            <img src={logo} alt="Al Nasser Group" className={styles.logo} />
          </Link>
          
          {/* Mobile Menu Toggle */}
          <button 
            className={styles.menuToggle} 
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span className={`${styles.hamburger} ${isMenuOpen ? styles.hamburgerOpen : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMenuOpen && (
        <div className={styles.menuBackdrop} onClick={closeMenu}></div>
      )}

      {/* Navigation Menu Bar */}
      <div className={styles.navbarMain}>
        <div className={styles.navbarContainer}>
          <div className={`${styles.navbarMenu} ${isMenuOpen ? styles.navbarMenuOpen : ''}`}>
            {/* Mobile Menu Close Button */}
            {isMenuOpen && (
              <button 
                className={styles.menuCloseButton}
                onClick={closeMenu}
                aria-label="Close menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
            {i18n.language === 'ar' ? (
              // Arabic: Reverse order - Contact Us first, Home last (desktop)
              // Mobile: Home first, Contact Us last (reordered via CSS)
              <>
                <Link to="/contact-us" data-nav-order="10" className={`${styles.navLink} ${isActive('/contact-us')}`} onClick={closeMenu}>
                  <span>{t('nav.contact')}</span>
                </Link>
                <Link to="/gallery" data-nav-order="9" className={`${styles.navLink} ${isActive('/gallery')}`} onClick={closeMenu}>
                  <span>{t('nav.gallery')}</span>
                </Link>
                <Link to="/media-center" data-nav-order="8" className={`${styles.navLink} ${isActive('/media-center')}`} onClick={closeMenu}>
                  <span>{t('nav.media')}</span>
                </Link>
                <Link to="/success-partners" data-nav-order="7" className={`${styles.navLink} ${isActive('/success-partners')}`} onClick={closeMenu}>
                  <span>{t('nav.partners')}</span>
                </Link>
                <Link to="/our-factory" data-nav-order="6" className={`${styles.navLink} ${isActive('/our-factory')}`} onClick={closeMenu}>
                  <span>{t('nav.factory')}</span>
                </Link>
                <Link to="/investor-relations" data-nav-order="5" className={`${styles.navLink} ${isActive('/investor-relations')}`} onClick={closeMenu}>
                  <span>{t('nav.investor')}</span>
                </Link>
                <Link to="/products" data-nav-order="4" className={`${styles.navLink} ${isActive('/products')}`} onClick={closeMenu}>
                  <span>{t('nav.products')}</span>
                </Link>
                <Link to="/board-of-directors" data-nav-order="3" className={`${styles.navLink} ${isActive('/board-of-directors')}`} onClick={closeMenu}>
                  <span>{t('nav.board')}</span>
                </Link>
                <Link to="/about" data-nav-order="2" className={`${styles.navLink} ${isActive('/about')}`} onClick={closeMenu}>
                  <span>{t('nav.about')}</span>
                </Link>
                <Link to="/" data-nav-order="1" className={`${styles.navLink} ${isActive('/')}`} onClick={closeMenu}>
                  <span>{t('nav.home')}</span>
                </Link>
              </>
            ) : (
              // English: Normal order - Home first, Contact Us last
              <>
                <Link to="/" className={`${styles.navLink} ${isActive('/')}`} onClick={closeMenu}>
                  <span>{t('nav.home')}</span>
                </Link>
                <Link to="/about" className={`${styles.navLink} ${isActive('/about')}`} onClick={closeMenu}>
                  <span>{t('nav.about')}</span>
                </Link>
                <Link to="/board-of-directors" className={`${styles.navLink} ${isActive('/board-of-directors')}`} onClick={closeMenu}>
                  <span>{t('nav.board')}</span>
                </Link>
                <Link to="/products" className={`${styles.navLink} ${isActive('/products')}`} onClick={closeMenu}>
                  <span>{t('nav.products')}</span>
                </Link>
                <Link to="/investor-relations" className={`${styles.navLink} ${isActive('/investor-relations')}`} onClick={closeMenu}>
                  <span>{t('nav.investor')}</span>
                </Link>
                <Link to="/our-factory" className={`${styles.navLink} ${isActive('/our-factory')}`} onClick={closeMenu}>
                  <span>{t('nav.factory')}</span>
                </Link>
                <Link to="/success-partners" className={`${styles.navLink} ${isActive('/success-partners')}`} onClick={closeMenu}>
                  <span>{t('nav.partners')}</span>
                </Link>
                <Link to="/media-center" className={`${styles.navLink} ${isActive('/media-center')}`} onClick={closeMenu}>
                  <span>{t('nav.media')}</span>
                </Link>
                <Link to="/gallery" className={`${styles.navLink} ${isActive('/gallery')}`} onClick={closeMenu}>
                  <span>{t('nav.gallery')}</span>
                </Link>
                <Link to="/contact-us" className={`${styles.navLink} ${isActive('/contact-us')}`} onClick={closeMenu}>
                  <span>{t('nav.contact')}</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;

